import type { BigScreenQueue } from './big-screen-queue';

/** Public scoreboard for the seeded rifle tournament. */
export const RIFLE_TOURNAMENT_SLUG = 'rifle-shooting-2026';
export const RIFLE_TOURNAMENT_ID = 'seed-shooting-rifle-2026';

/**
 * Whole-number shots (0–10, stored as tenths) for the seeded rifle row.
 * Other ids that reuse the slug, including tests, stay on decimal scoring.
 */
export function wholeNumberScoring(tournament: { id?: string | null } | null | undefined): boolean {
  return tournament?.id === RIFLE_TOURNAMENT_ID;
}

/** How often the public board refetches while the live connection is down. */
export const SCORING_POLL_MS = 3000;

/**
 * Public scoring responses must not use the ~60s cache on other public GETs.
 * CDN-Cache-Control stops the Cloudflare edge even if a browser cache directive differs.
 */
export const SCORING_PUBLIC_CACHE_CONTROL = 'no-store';

/**
 * The list of published tournaments (home page button, /scoreboard): a short browser cache is fine,
 * the edge still does not cache it.
 */
export const SCORING_LIST_HEADERS = {
  'Cache-Control': 'public, max-age=30',
  'CDN-Cache-Control': 'no-store',
} as const;

export const SCORING_PUBLIC_HEADERS = {
  'Cache-Control': SCORING_PUBLIC_CACHE_CONTROL,
  'CDN-Cache-Control': SCORING_PUBLIC_CACHE_CONTROL,
} as const;

const SERIES_MAX_TENTHS = 2000;
const SHOT_MAX_TENTHS = 109;
const SERIES_MAX = 30;
const SHOT_MAX = 60;
const SERIES_INNER_MAX = 60;

/** A shot of 10.0 or more is a "ten" for the tie-break. */
export const TEN_TENTHS = 100;

/**
 * Each tournament is one round: every shooter fires `shotsPerShooter` shots and the best `countedShots`
 * count (the rest are dropped). Admins set both per tournament (migration 0018); the defaults are the
 * 10.9 Rifle Shooting format, 15 shots / best 10, max 109.0. Shots are stored as series 1.
 */
export const SHOTS_PER_ROUND = 15;
export const COUNTED_SHOTS = 10;
export const SHOTS_PER_SHOOTER_MAX = 60;
export const ROUND_SERIES_NUMBER = 1;
export const ROUND_MAX_TENTHS = COUNTED_SHOTS * SHOT_MAX_TENTHS;

export interface ShootingFormat {
  shotsPerShooter: number;
  countedShots: number;
}

export const DEFAULT_FORMAT: ShootingFormat = { shotsPerShooter: SHOTS_PER_ROUND, countedShots: COUNTED_SHOTS };

export interface StoredTournament {
  id: string;
  slug: string;
  title: string;
  status: string;
  /** Missing on rows read before migration 0018: treated as 15. */
  shotsPerShooter?: number;
  /** Missing on rows read before migration 0018: treated as 10. */
  countedShots?: number;
  /** Event whose registrations feed this tournament; null = the event with the same slug. */
  eventId?: string | null;
  /** Listed on /scoreboard and the home page. Missing on rows read before migration 0020: unpublished. */
  published?: boolean;
  /** Big-screen board page (0-based) chosen by an admin. Missing before migration 0021: page 1. */
  displayPage?: number;
  /** Null means 6 shooters per Round 1 team (migration 0034). */
  round1TeamSize?: number | null;
  /** Legacy format option retained for database compatibility (migration 0034). */
  round1TrialShots?: number | null;
  /** Null means the top 6 Round 1 teams go to Round 2 (migration 0034). */
  round2QualifyingTeams?: number | null;
  round2ShotsPerShooter?: number | null;
  bigScreenRound?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Men / Women tag used by the board filter. */
export type ShooterGender = 'male' | 'female';
export const SHOOTER_GENDERS: readonly ShooterGender[] = ['male', 'female'];
export const SHOOTER_GENDER_LABELS: Record<ShooterGender, string> = { male: 'Men', female: 'Women' };

/** 'male' / 'female' (also m, f, men, women…) → the tag; anything else (other, blank, junk) → null. */
export function toShooterGender(value: unknown): ShooterGender | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (['male', 'm', 'man', 'men'].includes(v)) return 'male';
  if (['female', 'f', 'woman', 'women'].includes(v)) return 'female';
  return null;
}

/** API input: '' or null clears it; male / female set it; anything else is refused. */
export function parseShooterGender(value: unknown): { ok: true; gender: ShooterGender | null } | { ok: false; error: string } {
  if (value == null || value === '') return { ok: true, gender: null };
  const gender = toShooterGender(value);
  return gender ? { ok: true, gender } : fail('Choose Men, Women, or leave it blank.');
}

export interface StoredShooter {
  id: string;
  tournamentId: string;
  name: string;
  bib: string | null;
  sortOrder: number;
  createdAt: string;
  /** Null / missing = not set. */
  gender?: ShooterGender | null;
}

/**
 * One shot (`shotNumber` 1-15), or a whole-series total (`shotNumber` 0) typed with the old form before
 * per-shot scoring. Production had none when this changed; any that exist count only until shots are entered.
 */
export interface StoredScore {
  id: string;
  tournamentId: string;
  shooterId: string;
  seriesNumber: number;
  shotNumber: number;
  pointsTenths: number;
  innerTens: number;
  createdAt: string;
  updatedAt: string;
}

export interface LeaderboardLatest {
  seriesNumber: number;
  shotNumber: number;
  pointsDisplay: string;
  innerTens: number;
  label: string;
  updatedAt: string;
}

export interface LeaderboardShot {
  shotNumber: number;
  pointsDisplay: string;
  /** False for the shots dropped from the best 10. */
  counted: boolean;
}

export interface LeaderboardRow {
  /** Admin-only Round 2 data for the results export. */
  round2?: { team: string; shots: string[]; total: string; teamRank: number; overall: string } | null;
  rank: number;
  shooterId: string;
  name: string;
  bib: string | null;
  teamName?: string | null;
  /** Men / Women tag; null = not set. */
  gender: ShooterGender | null;
  totalDisplay: string;
  /** Inner 10s typed with old series totals. Kept so older screens still read the payload. */
  innerTens: number;
  /** Counted shots (best 10) of 10.0 or more. */
  tens: number;
  /** Sum of the dropped shots, shown with one decimal. */
  droppedDisplay: string;
  /** Shots that count (10). */
  shotsCounted: number;
  shotsFired: number;
  shotsPlanned: number;
  shots: LeaderboardShot[];
  latest: LeaderboardLatest | null;
}

export interface PublicLeaderboard {
  ok: true;
  tournament: {
    id: string;
    slug: string;
    title: string;
    status: string;
    shotsPlanned: number;
    shotsCounted: number;
    maxDisplay: string;
  };
  updatedAt: string | null;
  /** Page (0-based, BOARD_PAGE_SIZE ranks each) that the laptop board shows; set by an admin. */
  displayPage: number;
  /**
   * Round 1 TV board. Theme is stored on the linked event (null in the database means dark).
   * Qualifier counts are the Round 2 defaults unless a later change stores them.
   */
  bigScreen?: {
    theme: 'dark' | 'light';
    /** Original setting, before the legacy dark fallback; null lets the rifle TV default to light. */
    storedTheme?: 'dark' | 'light' | null;
    round?: 1 | 2;
    qualifiers: { male: number; female: number };
    /** How many Round 1 teams go to Round 2. The team board draws its cut after this many. */
    teamCut?: number;
    /** Shooters with no Male / Female tag. The TV leaves them off; only an admin is shown the count. */
    missingGender: number;
    eventId: string | null;
  };
  /**
   * Round 1 team standings for the rifle tournament. Omitted for every other event.
   * `tie` is set when the last Round 2 place is shared and an admin still has to choose.
   */
  round1?: {
    qualifyingTeams: number;
    teams: {
      rank: number;
      teamId: string;
      name: string;
      totalDisplay: string;
      tens: number;
      memberCount: number;
      qualifies?: boolean;
      members: {
        shooterId: string;
        name: string;
        bib: string | null;
        gender: ShooterGender | null;
        totalDisplay: string;
        shotsFired: number;
      }[];
    }[];
    tie: { slots: number; teamIds: string[] } | null;
  };
  rows: LeaderboardRow[];
  /**
   * Shooting queue for the big screen and the public board.
   * Omitted while the event's queue panel is off, so the boards stay full width.
   */
  queue?: BigScreenQueue;
  round2Queue?: BigScreenQueue;
  /**
   * Round 2 individual and team standings. Omitted unless an admin has turned on
   * "Show Round 2 on public leaderboard" for rifle. Absent means the public board is unchanged.
   */
  round2?: {
    /** Latest eligible Round 2 score save, independent of Round 1. */
    updatedAt?: string | null;
    shotsPlanned: number;
    shotsCounted: number;
    maxDisplay: string;
    qualifyingTeams: number;
    teams: {
      rank: number;
      teamId: string;
      name: string;
      totalDisplay: string;
      round1TotalDisplay: string;
      overallDisplay: string;
      memberCount: number;
      members: {
        shooterId: string;
        name: string;
        bib: string | null;
        gender: ShooterGender | null;
        totalDisplay: string;
        shotsFired: number;
        /** Read-only detail for the TV analytics; omitted by older API versions. */
        shots?: LeaderboardShot[];
        latestUpdatedAt?: string | null;
      }[];
    }[];
  };
}

export interface ParsedScore {
  seriesNumber: number;
  shotNumber: number;
  pointsTenths: number;
  innerTens: number;
}

export interface ParsedShot {
  shotNumber: number;
  pointsTenths: number;
}

export function scoringWebSocketUrl(httpUrl: string): string {
  if (httpUrl.startsWith('https://')) return `wss://${httpUrl.slice('https://'.length)}`;
  if (httpUrl.startsWith('http://')) return `ws://${httpUrl.slice('http://'.length)}`;
  return httpUrl;
}

/** Poll only after the live socket has dropped. An open socket is the source of updates. */
export function shouldPollLeaderboard(connection: 'connecting' | 'open' | 'polling'): boolean {
  return connection === 'polling';
}

export function formatPointsTenths(tenths: number): string {
  const sign = tenths < 0 ? '-' : '';
  const abs = Math.abs(tenths);
  const whole = Math.floor(abs / 10);
  const frac = abs % 10;
  if (frac === 0) return `${sign}${whole}`;
  return `${sign}${whole}.${frac}`;
}

/** Decimal scoring always shows one decimal: 10.0, 98.4, 163.5. */
export function formatDecimalTenths(tenths: number): string {
  const sign = tenths < 0 ? '-' : '';
  const abs = Math.abs(Math.round(tenths));
  return `${sign}${Math.floor(abs / 10)}.${abs % 10}`;
}

export const ROUND_MAX_DISPLAY = formatDecimalTenths(ROUND_MAX_TENTHS);

function wholeIn(value: unknown, min: number, max: number): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : null;
}

/** The tournament's format, with the rifle defaults for anything missing and counted capped at shots. */
export function tournamentFormat(tournament: Partial<Pick<StoredTournament, 'shotsPerShooter' | 'countedShots'>>): ShootingFormat {
  const shotsPerShooter = wholeIn(tournament.shotsPerShooter, 1, SHOTS_PER_SHOOTER_MAX) ?? DEFAULT_FORMAT.shotsPerShooter;
  const counted = wholeIn(tournament.countedShots, 1, SHOTS_PER_SHOOTER_MAX) ?? Math.min(DEFAULT_FORMAT.countedShots, shotsPerShooter);
  return { shotsPerShooter, countedShots: Math.min(counted, shotsPerShooter) };
}

/** The format from an API tournament (`shotsPlanned` / `shotsCounted`), with the rifle defaults when missing. */
export function summaryFormat(tournament: { shotsPlanned?: number; shotsCounted?: number }): ShootingFormat {
  return tournamentFormat({ shotsPerShooter: tournament.shotsPlanned, countedShots: tournament.shotsCounted });
}

export function formatMaxDisplay(format: ShootingFormat): string {
  return formatDecimalTenths(format.countedShots * SHOT_MAX_TENTHS);
}

/** "Best 10 of 15 shots count" or "All 10 shots count". */
export function formatRuleText(format: ShootingFormat): string {
  return format.countedShots < format.shotsPerShooter
    ? `Best ${format.countedShots} of ${format.shotsPerShooter} shots count`
    : `All ${format.shotsPerShooter} shots count`;
}

/** "Ties: more 10s, then higher dropped shots, then the last shot back." (dropped step only with drops) */
export function formatTieText(format: ShootingFormat): string {
  return format.countedShots < format.shotsPerShooter
    ? 'Ties: more 10s, then higher dropped shots, then the last shot back.'
    : 'Ties: more 10s, then the last shot back.';
}

/** Validate an admin's format change: shots 1-60, counted 1..shots (blank counted = all shots). */
export function parseFormatInput(input: {
  shotsPerShooter: unknown;
  countedShots: unknown;
}): { ok: true; format: ShootingFormat } | { ok: false; error: string } {
  const shots = parseWholeNumber(input.shotsPerShooter, 'Shots per shooter', 1, SHOTS_PER_SHOOTER_MAX);
  if (isParseError(shots)) return shots;
  const countedRaw = input.countedShots == null || input.countedShots === '' ? shots.n : input.countedShots;
  const counted = parseWholeNumber(countedRaw, 'Best shots that count', 1, shots.n);
  if (isParseError(counted)) return counted;
  return { ok: true, format: { shotsPerShooter: shots.n, countedShots: counted.n } };
}

export function scoreLabel(shotNumber: number): string {
  return shotNumber > 0 ? `Shot ${shotNumber}` : 'Earlier total';
}

export function isPublicScoreboardPath(pathname: string): boolean {
  return pathname === '/scoreboard' || pathname.startsWith('/scoreboard/');
}

function fail(error: string): { ok: false; error: string } {
  return { ok: false, error };
}

/** `strict` is off in this repo, so `if (!result.ok)` does not narrow. */
export function isParseError<T extends { ok: boolean }>(
  result: T,
): result is Extract<T, { ok: false }> {
  return result.ok === false;
}

export function parsePointsInput(
  value: unknown,
  maxTenths: number,
): { ok: true; tenths: number } | { ok: false; error: string } {
  const raw = typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : typeof value === 'string'
      ? value.trim()
      : '';
  if (!raw) return fail('Enter the score.');
  if (!/^\d+(\.\d)?$/.test(raw)) return fail('Use a whole number, or one decimal place like 10.9.');
  const [whole, frac = ''] = raw.split('.');
  const tenths = Number(whole) * 10 + (frac === '' ? 0 : Number(frac));
  if (tenths > maxTenths) {
    return fail(
      maxTenths === SHOT_MAX_TENTHS
        ? 'One shot can be at most 10.9.'
        : `A series total can be at most ${formatPointsTenths(maxTenths)}.`,
    );
  }
  return { ok: true, tenths };
}

export const SHOT_RANGE_MESSAGE = 'A shot is 0.0 to 10.9, with one decimal (type 0 for a miss).';

/**
 * One decimal-scored shot: 0.0 to 10.9. "10" means 10.0 and "9,7" (comma keypads) means 9.7.
 * Returns tenths (10.9 -> 109).
 */
export function parseShotPoints(value: unknown): { ok: true; tenths: number } | { ok: false; error: string } {
  const raw = typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : typeof value === 'string'
      ? value.trim().replace(',', '.')
      : '';
  if (!raw) return fail('Enter the shot.');
  const match = /^(\d{1,2})(?:\.(\d))?$/.exec(raw);
  if (!match) return fail(SHOT_RANGE_MESSAGE);
  const tenths = Number(match[1]) * 10 + (match[2] ? Number(match[2]) : 0);
  if (tenths > SHOT_MAX_TENTHS) return fail(SHOT_RANGE_MESSAGE);
  return { ok: true, tenths };
}

function parseWholeNumber(
  value: unknown,
  label: string,
  min: number,
  max: number,
): { ok: true; n: number } | { ok: false; error: string } {
  const raw = typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : typeof value === 'string'
      ? value.trim()
      : '';
  if (!raw) return fail(`Enter ${label}.`);
  if (!/^\d+$/.test(raw)) return fail(`${label} must be a whole number.`);
  const n = Number(raw);
  if (n < min || n > max) return fail(`${label} must be from ${min} to ${max}.`);
  return { ok: true, n };
}

export const WHOLE_SHOT_MESSAGE = 'A shot is a whole number from 0 to 10. Type 0 for a miss.';

/** Whole number 0–10 stored as tenths (7 → 70, 10 → 100). Decimals are refused. */
export function parseWholeShotPoints(value: unknown): { ok: true; tenths: number } | { ok: false; error: string } {
  const raw =
    typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : typeof value === 'string'
        ? value.trim()
        : '';
  if (!raw) return fail('Enter the shot.');
  if (!/^\d{1,2}$/.test(raw)) return fail(WHOLE_SHOT_MESSAGE);
  const n = Number(raw);
  if (n > 10) return fail(WHOLE_SHOT_MESSAGE);
  return { ok: true, tenths: n * 10 };
}

/** One shot of the round. Decimal 0.0–10.9, or a whole number 0–10 when `whole` is set. */
export function parseShotInput(
  input: { shotNumber: unknown; points: unknown },
  shotsPerShooter: number = SHOTS_PER_ROUND,
  whole = false,
): { ok: true; value: ParsedShot } | { ok: false; error: string } {
  const shot = parseWholeNumber(input.shotNumber, 'The shot', 1, shotsPerShooter);
  if (isParseError(shot)) return shot;
  const points = whole ? parseWholeShotPoints(input.points) : parseShotPoints(input.points);
  if (isParseError(points)) return points;
  return { ok: true, value: { shotNumber: shot.n, pointsTenths: points.tenths } };
}

/** Old series-total entry (no longer accepted by the API). */
export function parseScoreInput(input: {
  seriesNumber: unknown;
  shotNumber?: unknown;
  points: unknown;
  innerTens: unknown;
}): { ok: true; value: ParsedScore } | { ok: false; error: string } {
  const series = parseWholeNumber(input.seriesNumber, 'The series', 1, SERIES_MAX);
  if (isParseError(series)) return series;
  const shotRaw = input.shotNumber == null || input.shotNumber === '' ? 0 : input.shotNumber;
  const shot = parseWholeNumber(shotRaw, 'The shot', 0, SHOT_MAX);
  if (isParseError(shot)) return shot;
  const isShot = shot.n > 0;
  const points = parsePointsInput(input.points, isShot ? SHOT_MAX_TENTHS : SERIES_MAX_TENTHS);
  if (isParseError(points)) return points;
  const inner = parseWholeNumber(
    input.innerTens == null || input.innerTens === '' ? 0 : input.innerTens,
    'Inner 10s',
    0,
    isShot ? 1 : SERIES_INNER_MAX,
  );
  if (isParseError(inner)) {
    if (isShot) return fail('A single shot has 0 or 1 inner 10.');
    return inner;
  }
  return {
    ok: true,
    value: {
      seriesNumber: series.n,
      shotNumber: shot.n,
      pointsTenths: points.tenths,
      innerTens: inner.n,
    },
  };
}

export function parseShooterName(value: unknown): { ok: true; name: string } | { ok: false; error: string } {
  if (typeof value !== 'string') return fail('Enter the shooter name.');
  const name = value.trim().replace(/\s+/g, ' ');
  if (!name) return fail('Enter the shooter name.');
  if (name.length > 80) return fail('That name is too long.');
  return { ok: true, name };
}

export const RFL_PHONE_MESSAGE =
  'That looks like a phone number. Enter the RFL number from the registration, like RFL-0091, or leave it empty.';
export const RFL_FORMAT_MESSAGE = 'Enter the RFL number like RFL-0091 (or just 91), or leave it empty.';
export const DEFAULT_BIB_PREFIX = 'RFL';

/** Registration prefix such as RFL or TT (letters and digits only), else the default RFL. */
export function normalizeBibPrefix(prefix: string | null | undefined): string {
  const cleaned = (prefix ?? '').trim().toUpperCase();
  return /^[A-Z][A-Z0-9]{0,9}$/.test(cleaned) ? cleaned : DEFAULT_BIB_PREFIX;
}

export function bibPhoneMessage(prefix: string): string {
  return `That looks like a phone number. Enter the ${prefix} number from the registration, like ${prefix}-0091, or leave it empty.`;
}

export function bibFormatMessage(prefix: string): string {
  return `Enter the ${prefix} number like ${prefix}-0091 (or just 91), or leave it empty.`;
}

/**
 * Optional registration number for a shooter added by hand. With prefix RFL it accepts "RFL-0091",
 * "rfl 91" or "91" and stores "RFL-0091". Anything that looks like a phone number (10+ digits) is refused.
 */
export function parseBib(
  value: unknown,
  prefixInput: string = DEFAULT_BIB_PREFIX,
): { ok: true; bib: string | null } | { ok: false; error: string } {
  const prefix = normalizeBibPrefix(prefixInput);
  if (value == null || value === '') return { ok: true, bib: null };
  if (typeof value !== 'string') return fail(bibFormatMessage(prefix));
  const raw = value.trim();
  if (!raw) return { ok: true, bib: null };
  if (raw.replace(/\D/g, '').length >= 10) return fail(bibPhoneMessage(prefix));
  const escaped = prefix.replace(/[^A-Z0-9]/g, '');
  const match = new RegExp(`^(?:${escaped})?[\\s-]*(\\d{1,4})$`, 'i').exec(raw);
  if (!match) return fail(bibFormatMessage(prefix));
  const n = Number(match[1]);
  if (n < 1) return fail(bibFormatMessage(prefix));
  return { ok: true, bib: `${prefix}-${String(n).padStart(4, '0')}` };
}

/** RFL-0091 -> 91 (for the given prefix); anything else -> null. */
export function rflNumber(value: string | null | undefined, prefixInput: string = DEFAULT_BIB_PREFIX): number | null {
  const prefix = normalizeBibPrefix(prefixInput);
  const match = new RegExp(`^${prefix}-(\\d{1,6})$`, 'i').exec((value ?? '').trim());
  return match ? Number(match[1]) : null;
}

/**
 * Next free number with the event's prefix: one above the highest PREFIX-#### already used (by shooters
 * or registrations), stepping past any that are taken. Starts at PREFIX-0001.
 */
export function nextFreeRfl(taken: ReadonlyArray<string | null | undefined>, prefixInput: string = DEFAULT_BIB_PREFIX): string {
  const prefix = normalizeBibPrefix(prefixInput);
  const used = new Set<number>();
  for (const value of taken) {
    const n = rflNumber(value, prefix);
    if (n != null) used.add(n);
  }
  let next = used.size === 0 ? 1 : Math.max(...used) + 1;
  while (used.has(next)) next += 1;
  return `${prefix}-${String(next).padStart(4, '0')}`;
}

/**
 * Which shots are dropped: once more than 10 are entered, the lowest ones beyond the best 10.
 * Equal values: the later shot is dropped first (it does not change the total).
 */
export function droppedShotNumbers(
  shots: ReadonlyArray<{ shotNumber: number; pointsTenths: number }>,
  countedShots: number = COUNTED_SHOTS,
): Set<number> {
  const dropCount = Math.max(0, shots.length - countedShots);
  const lowestFirst = [...shots].sort((a, b) => a.pointsTenths - b.pointsTenths || b.shotNumber - a.shotNumber);
  return new Set(lowestFirst.slice(0, dropCount).map((shot) => shot.shotNumber));
}

/** Best-10 total of the shots so far (all of them while there are 10 or fewer). */
export function bestShotsTotal(
  shots: ReadonlyArray<{ shotNumber: number; pointsTenths: number }>,
  countedShots: number = COUNTED_SHOTS,
): {
  totalTenths: number;
  droppedTenths: number;
  dropped: Set<number>;
} {
  const dropped = droppedShotNumbers(shots, countedShots);
  let totalTenths = 0;
  let droppedTenths = 0;
  for (const shot of shots) {
    if (dropped.has(shot.shotNumber)) droppedTenths += shot.pointsTenths;
    else totalTenths += shot.pointsTenths;
  }
  return { totalTenths, droppedTenths, dropped };
}

export interface ShooterSummary {
  /** Best-10 total. */
  totalTenths: number;
  /** Sum of the dropped shots (second tie-break). */
  droppedTenths: number;
  shotsFired: number;
  /** Counted shots of 10.0 or more. */
  tens: number;
  innerTens: number;
  /** Every shot, by shot number. */
  shots: StoredScore[];
  /** Shot numbers that do not count. */
  dropped: Set<number>;
}

/**
 * Totals for one shooter: the best 10 of their shots. An old whole-series total (from before per-shot
 * scoring) stands in only while the shooter has no shots, and counts as a full round fired.
 */
export function summarizeShooter(rows: StoredScore[], format: ShootingFormat = DEFAULT_FORMAT): ShooterSummary {
  const shots = rows.filter((row) => row.shotNumber > 0).sort((a, b) => a.shotNumber - b.shotNumber);
  if (shots.length > 0) {
    const best = bestShotsTotal(shots, format.countedShots);
    return {
      totalTenths: best.totalTenths,
      droppedTenths: best.droppedTenths,
      shotsFired: shots.length,
      tens: shots.filter((shot) => !best.dropped.has(shot.shotNumber) && shot.pointsTenths >= TEN_TENTHS).length,
      innerTens: 0,
      shots,
      dropped: best.dropped,
    };
  }
  const legacy = rows.filter((row) => row.shotNumber === 0);
  return {
    totalTenths: legacy.reduce((sum, row) => sum + row.pointsTenths, 0),
    droppedTenths: 0,
    shotsFired: legacy.length > 0 ? format.shotsPerShooter : 0,
    tens: 0,
    innerTens: legacy.reduce((sum, row) => sum + row.innerTens, 0),
    shots: [],
    dropped: new Set(),
  };
}

/**
 * Tie-break. Higher wins at the first step that differs:
 *   1. best-10 total;
 *   2. number of counted shots of 10.0 or more;
 *   3. sum of the dropped shots;
 *   4. countback over all shots: shot 15, then shot 14, and so on back to shot 1
 *      (a shot not fired counts below 0.0).
 * If all of that is equal the shooters share the rank (1, 1, 3).
 * Returns < 0 when `a` ranks above `b`.
 */
export function compareForRank(a: ShooterSummary, b: ShooterSummary): number {
  if (a.totalTenths !== b.totalTenths) return b.totalTenths - a.totalTenths;
  if (a.tens !== b.tens) return b.tens - a.tens;
  if (a.droppedTenths !== b.droppedTenths) return b.droppedTenths - a.droppedTenths;
  const lastShot = Math.max(
    a.shots.reduce((max, shot) => Math.max(max, shot.shotNumber), 0),
    b.shots.reduce((max, shot) => Math.max(max, shot.shotNumber), 0),
  );
  for (let shot = lastShot; shot >= 1; shot -= 1) {
    const aPts = a.shots.find((row) => row.shotNumber === shot)?.pointsTenths ?? -1;
    const bPts = b.shots.find((row) => row.shotNumber === shot)?.pointsTenths ?? -1;
    if (aPts !== bPts) return bPts - aPts;
  }
  return 0;
}

function latestScore(rows: StoredScore[]): StoredScore | null {
  let latest: StoredScore | null = null;
  for (const row of rows) {
    if (!latest) {
      latest = row;
      continue;
    }
    if (row.updatedAt > latest.updatedAt) latest = row;
    else if (row.updatedAt === latest.updatedAt && row.shotNumber > latest.shotNumber) latest = row;
  }
  return latest;
}

export function buildLeaderboard(
  tournament: StoredTournament,
  shooters: StoredShooter[],
  scores: StoredScore[],
): PublicLeaderboard {
  const format = tournamentFormat(tournament);
  const whole = wholeNumberScoring(tournament);
  const showPoints = (tenths: number) => (whole && tenths % 10 === 0 ? formatPointsTenths(tenths) : formatDecimalTenths(tenths));
  const byShooter = new Map<string, StoredScore[]>();
  for (const score of scores) {
    if (score.tournamentId !== tournament.id) continue;
    const list = byShooter.get(score.shooterId) ?? [];
    list.push(score);
    byShooter.set(score.shooterId, list);
  }

  const ranked = shooters
    .filter((shooter) => shooter.tournamentId === tournament.id)
    .map((shooter) => {
      const rows = byShooter.get(shooter.id) ?? [];
      return { shooter, summary: summarizeShooter(rows, format), latest: latestScore(rows) };
    })
    .sort((a, b) => {
      const aScored = a.latest != null;
      const bScored = b.latest != null;
      if (aScored !== bScored) return aScored ? -1 : 1;
      if (aScored) {
        const order = compareForRank(a.summary, b.summary);
        if (order !== 0) return order;
      }
      const bib = (a.shooter.bib ?? '').localeCompare(b.shooter.bib ?? '');
      if (bib !== 0) return bib;
      return a.shooter.name.localeCompare(b.shooter.name, undefined, { sensitivity: 'base' });
    });

  let updatedAt: string | null = null;
  let scoredSoFar = 0;
  let lastRank = 0;
  let previous: (typeof ranked)[number] | null = null;
  const rows: LeaderboardRow[] = ranked.map((entry) => {
    if (entry.latest && (!updatedAt || entry.latest.updatedAt > updatedAt)) {
      updatedAt = entry.latest.updatedAt;
    }
    let rank = 0;
    if (entry.latest) {
      scoredSoFar += 1;
      rank = previous && compareForRank(previous.summary, entry.summary) === 0 ? lastRank : scoredSoFar;
      lastRank = rank;
      previous = entry;
    }
    const { summary } = entry;
    return {
      rank,
      shooterId: entry.shooter.id,
      name: entry.shooter.name,
      bib: entry.shooter.bib,
      gender: entry.shooter.gender ?? null,
      totalDisplay: showPoints(summary.totalTenths),
      innerTens: summary.innerTens,
      tens: summary.tens,
      droppedDisplay: showPoints(summary.droppedTenths),
      shotsFired: summary.shotsFired,
      shotsPlanned: format.shotsPerShooter,
      shotsCounted: format.countedShots,
      shots: summary.shots.map((shot) => ({
        shotNumber: shot.shotNumber,
        pointsDisplay: showPoints(shot.pointsTenths),
        counted: !summary.dropped.has(shot.shotNumber),
      })),
      latest: entry.latest
        ? {
            seriesNumber: entry.latest.seriesNumber,
            shotNumber: entry.latest.shotNumber,
            pointsDisplay: showPoints(entry.latest.pointsTenths),
            innerTens: entry.latest.innerTens,
            label: scoreLabel(entry.latest.shotNumber),
            updatedAt: entry.latest.updatedAt,
          }
        : null,
    };
  });

  return {
    ok: true,
    tournament: {
      id: tournament.id,
      slug: tournament.slug,
      title: tournament.title,
      status: tournament.status,
      shotsPlanned: format.shotsPerShooter,
      shotsCounted: format.countedShots,
      maxDisplay: whole ? String(format.countedShots * 10) : formatMaxDisplay(format),
    },
    updatedAt,
    displayPage: clampBoardPage(tournament.displayPage ?? 0, rows.length),
    rows,
  };
}

/** Ranks per big-screen page. */
export const BOARD_PAGE_SIZE = 10;

export function boardPageCount(rowCount: number): number {
  return Math.max(1, Math.ceil(rowCount / BOARD_PAGE_SIZE));
}

/** A page index that exists for this many rows (0 when out of range or not a whole number). */
export function clampBoardPage(page: number, rowCount: number): number {
  if (!Number.isInteger(page) || page < 0) return 0;
  return Math.min(page, boardPageCount(rowCount) - 1);
}

/** "Showing ranks 1–10 of 90" for a page. */
export function boardPageText(page: number, rowCount: number): string {
  if (rowCount === 0) return 'No shooters yet';
  const safe = clampBoardPage(page, rowCount);
  const first = safe * BOARD_PAGE_SIZE + 1;
  const last = Math.min(rowCount, first + BOARD_PAGE_SIZE - 1);
  return `Showing ranks ${first}–${last} of ${rowCount}`;
}

export function broadcastText(sockets: Array<{ send(data: string): void }>, text: string): void {
  for (const socket of sockets) {
    try {
      socket.send(text);
    } catch {
      // A viewer can disconnect between the list and the send.
    }
  }
}
